import { Point } from "../types/Custom/Types"

// Helper function to draw grid lines on a canvas
export const drawGridLines = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
  ctx.strokeStyle = "white"
  ctx.lineWidth = 0.7
  const cellSize = 80

  for (let c = 1; c < canvas.width / cellSize; c++) {
    ctx.beginPath()
    ctx.moveTo(c * cellSize, 0)
    ctx.lineTo(c * cellSize, canvas.height)
    ctx.stroke()
  }

  for (let r = 1; r < canvas.height / cellSize; r++) {
    ctx.beginPath()
    ctx.moveTo(0, r * cellSize)
    ctx.lineTo(canvas.width, r * cellSize)
    ctx.stroke()
  }
}

// Helper function to get the region label based on its state and index
export const getRegionLabel = (state: number, index: number): string => {
  if (state === 0) return `In (${index})`
  if (state === 1) return `Out (${index})`
  if (state === 2) return `Dwelling (${index})`
  if (state === 8) return `Cashier (${index})`
  if (state === 9) return `Zone (${index})`

  return ""
}

// Helper function to draw a tooltip on a canvas
export const drawTooltip = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, width?: number) => {
  const tooltipWidth = width || 70
  const tooltipHeight = 29.5
  const fontSize = 12
  const fontFamily = "Inter"
  const arrowHeight = 5
  const arrowWidth = 10

  // Set font and alignment for the text
  ctx.font = `${fontSize}px ${fontFamily}`
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"

  // Draw the rounded rectangle for tooltip background
  ctx.fillStyle = "rgba(0,0,0,0.6)" // Tooltip background color
  ctx.beginPath()
  ctx.moveTo(x - tooltipWidth / 2 + 5, y - tooltipHeight) // Start top-left corner
  ctx.arcTo(x + tooltipWidth / 2, y - tooltipHeight, x + tooltipWidth / 2, y - tooltipHeight + 5, 5) // Top-right corner
  ctx.arcTo(x + tooltipWidth / 2, y, x + tooltipWidth / 2 - 5, y, 5) // Bottom-right corner
  ctx.lineTo(x + arrowWidth / 2, y) // Bottom-right before triangle
  ctx.lineTo(x, y + arrowHeight) // Bottom point of triangle
  ctx.lineTo(x - arrowWidth / 2, y) // Bottom-left after triangle
  ctx.arcTo(x - tooltipWidth / 2, y, x - tooltipWidth / 2, y - tooltipHeight + 5, 5) // Bottom-left corner
  ctx.arcTo(x - tooltipWidth / 2, y - tooltipHeight, x - tooltipWidth / 2 + 5, y - tooltipHeight, 5) // Top-left corner
  ctx.closePath()
  ctx.fill()

  // Draw the text in the tooltip
  ctx.fillStyle = "#FFF" // Text color
  ctx.fillText(text, x, y - tooltipHeight / 2)
}

// Utility function to draw numbered circles at each point
export const drawPointCircles = (ctx: CanvasRenderingContext2D, points: Point[]) => {
  points.forEach((point, index) => {
    // Circle properties
    const circleRadius = 7
    const circleX = point.x
    const circleY = point.y

    // Draw circle
    ctx.beginPath()
    ctx.fillStyle = "#FFFF"
    ctx.arc(circleX, circleY, circleRadius, 0, 2 * Math.PI)
    ctx.fill()

    // Draw the text (number inside the circle)
    ctx.font = "12px Inter"
    ctx.fillStyle = "#000"
    ctx.textAlign = "center"
    ctx.textBaseline = "middle"
    ctx.fillText(`${index + 1}`, circleX, circleY)
  })
}

/**
 * Draws a line with an arrowhead from a starting point to an ending point on a given canvas context.
 *
 * @param ctx - The 2D rendering context for the drawing surface of a <canvas> element.
 * @param from - The starting point of the line, with x and y coordinates.
 * @param to - The ending point of the line, with x and y coordinates.
 * @param lineColor - The color of the line.
 * @param lineWidth - The width of the line.
 * @param headLength - The length of the arrowhead in pixels.
 */
export function drawLineWithArrowhead(
  ctx: CanvasRenderingContext2D,
  from: { x: number; y: number },
  to: { x: number; y: number },
  lineColor: string = "white",
  lineWidth: number = 2,
  headLength: number = 10
): void {
  const angle = Math.atan2(to.y - from.y, to.x - from.x)

  // Draw the line
  ctx.strokeStyle = lineColor
  ctx.lineWidth = lineWidth
  ctx.beginPath()
  ctx.moveTo(from.x, from.y)
  ctx.lineTo(to.x, to.y)
  ctx.stroke()

  // Draw the arrowhead
  ctx.beginPath()
  ctx.moveTo(to.x, to.y)
  ctx.lineTo(to.x - headLength * Math.cos(angle - Math.PI / 6), to.y - headLength * Math.sin(angle - Math.PI / 6))
  ctx.moveTo(to.x, to.y)
  ctx.lineTo(to.x - headLength * Math.cos(angle + Math.PI / 6), to.y - headLength * Math.sin(angle + Math.PI / 6))
  ctx.stroke()
}

// colors available in SUI
const AVAILABLE_COLORS = ["green", "pink", "purple", "yellow", "gray", "red", "orange", "blue"] as const

/**
 * Generates a random color CSS variable from a predefined list of colors
 * @returns {string} A CSS variable in the format '--{color}-text-2'
 * @example
 * const color = generateRandomTextColor(); // '--blue-text-2'
 */
export const generateRandomTextColor = (): string => {
  const randomIndex = Math.floor(Math.random() * AVAILABLE_COLORS.length)
  return `--${AVAILABLE_COLORS[randomIndex]}-text-2`
}

/**
 * Gets a sequential color CSS variable from a predefined list of colors
 * Ensures the first 8 items get unique colors, then cycles through colors
 * @param {number} index - The index of the item (0-based)
 * @returns {string} A CSS variable in the format 'var(--{color}-background-2)'
 * @example
 * const color1 = getSequentialBackgroundColor(0); // 'var(--blue-background-2)'
 * const color2 = getSequentialBackgroundColor(1); // 'var(--gray-background-2)'
 * const color9 = getSequentialBackgroundColor(8); // 'var(--blue-background-2)' (cycles back)
 */
export const getSequentialBackgroundColor = (index: number): string => {
  const colorIndex = index % AVAILABLE_COLORS.length
  return `var(--${AVAILABLE_COLORS[colorIndex]}-background-2)`
}

/**
 * Gets a sequential color CSS variable from a predefined list of colors using border-1 variant
 * Ensures the first 8 items get unique colors, then cycles through colors
 * @param {number} index - The index of the item (0-based)
 * @returns {string} A CSS variable in the format 'var(--{color}-border-1)'
 * @example
 * const color1 = getSequentialBorderColor(0); // 'var(--blue-border-1)'
 * const color2 = getSequentialBorderColor(1); // 'var(--gray-border-1)'
 * const color9 = getSequentialBorderColor(8); // 'var(--blue-border-1)' (cycles back)
 */
export const getSequentialBorderColor = (index: number): string => {
  const colorIndex = index % AVAILABLE_COLORS.length
  return `var(--${AVAILABLE_COLORS[colorIndex]}-border-1)`
}

/**
 * Converts a CSS variable to its corresponding hex color value.
 * @param {string} cssVariable - The CSS variable (e.g., '--blue-text-2').
 * @returns {string} The hex color value (e.g., '#0000FF') or an empty string if the variable is not provided.
 */
export const getHexColorFromCSSVariable = (cssVariable?: string): string => {
  if (!cssVariable) return ""
  return getComputedStyle(document.documentElement).getPropertyValue(cssVariable).trim()
}

/**
 * Converts a hex color code to an rgba color string.
 * @param {string} hex - The hex color code (e.g., "#RRGGBB" or "#RGB").
 * @param {number} [opacity=1] - The opacity value (0 to 1). Default is 1.
 * @returns {string} The rgba color string (e.g., "rgba(255, 0, 0, 1)").
 */
export const hexToRgba = (hex: string, opacity: number = 1): string => {
  // Remove the hash at the start if it's there
  hex = hex.replace(/^#/, "")

  // Parse the r, g, b values
  let r, g, b
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16)
    g = parseInt(hex[1] + hex[1], 16)
    b = parseInt(hex[2] + hex[2], 16)
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16)
    g = parseInt(hex.substring(2, 4), 16)
    b = parseInt(hex.substring(4, 6), 16)
  }

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}
